import "@ant-design/v5-patch-for-react-19";
import AntdApp from "antd/es/app/App";
import ConfigProvider from "antd/es/config-provider";
import type { Locale } from "antd/es/locale";
import enUS from "antd/es/locale/en_US";
import jaJP from "antd/es/locale/ja_JP";
import koKR from "antd/es/locale/ko_KR";
import thTH from "antd/es/locale/th_TH";
import viVN from "antd/es/locale/vi_VN";
// https://ant.design/docs/react/replace-moment#Custom-component
// https://github.com/ant-design/ant-design/issues/26190#issuecomment-703673400
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import "dayjs/locale/th";
import "dayjs/locale/vi";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import i18n from "i18next";
import I18NextHttpBackend, {
  type HttpBackendOptions,
} from "i18next-http-backend";
import React, { Suspense, useEffect, useState } from "react";
import { initReactI18next, useTranslation } from "react-i18next";
import { RelayEnvironmentProvider } from "react-relay/hooks";
import { Navigate, Route, Routes } from "react-router-dom";

import "./App.css";
import { createRelayEnvironment } from "./RelayEnvironment";
import RequirePrivilege from "./components/contexts/RequirePrivilege";
import "./fonts.css";
import { usePreference } from "./hooks/atoms/preferences";
import { AuthProvider } from "./hooks/auth";

const MainLayout = React.lazy(() => import("./components/MainLayout"));

const FlexActivityIndicator = React.lazy(
  () => import("./components/FlexActivityIndicator")
);
const OutdatedBrowserAlert = React.lazy(
  () => import("./components/OutdatedBrowserAlert")
);

const AccessDenied = React.lazy(() => import("./components/AccessDenied"));
const RequireAuth = React.lazy(
  () => import("./components/contexts/RequireAuth")
);

const LoginPage = React.lazy(() => import("./screens/LoginPage"));
const NoMatch = React.lazy(() => import("./screens/NoMatch"));

const DashboardPage = React.lazy(() => import("./screens/DashboardPage"));
const PipelineJobDetail = React.lazy(
  () => import("./screens/PipelineJobDetail")
);
const PipelineJobsPage = React.lazy(() => import("./screens/PipelineJobsPage"));
const PipelineList = React.lazy(() => import("./screens/PipelineList"));
const PromptsPage = React.lazy(() => import("./screens/PromptsPage"));
const ModelDeploymentsPage = React.lazy(
  () => import("./screens/ModelDeploymentsPage")
);
const AuditLogsPage = React.lazy(() => import("./screens/AuditLogsPage"));
const SettingsPage = React.lazy(() => import("./screens/SettingsPage"));

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);

// TODO: for the reset relay if needed, move this to a separate component with all the providers.
// const myEnvironmentKey = new EnvironmentKey("Backend.AI Environment");

i18n
  .use(initReactI18next)
  .use(I18NextHttpBackend)
  .init<HttpBackendOptions>({
    backend: {
      loadPath: `/static/locales/{{lng}}/{{ns}}.json?v=${process.env.__VITE_BUILD_HASH__}`,
    },
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
    react: {
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ["br", "strong", "span", "code", "p"],
    },
  });

const useLocale = (): Locale => {
  const { language: locale } = usePreference();
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n
      .changeLanguage(locale)
      .then(() => dayjs.locale(locale))
      .catch((error) => {
        console.error("Error changing language:", error);
        dayjs.locale("en");
      });
  }, [locale]);

  switch (locale) {
    case "ko":
      // Until antd@5.28.2 is released
      // ref: https://github.com/ant-design/ant-design/pull/55672
      return {
        ...koKR,
        Text: {
          ...koKR.Text,
          copied: "복사됨",
        },
      };
    case "ja":
      return jaJP;
    case "th":
      return thTH;
    case "vi":
      return viVN;
    case "en":
    default:
      return enUS;
  }
};

const initialRelayEnvironment = createRelayEnvironment();

const App = () => {
  const locale = useLocale();

  const [relayEnvironment, setRelayEnvironment] = useState(
    initialRelayEnvironment
  );

  return (
    <AntdApp>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#1677FF",
            colorPrimaryBg: "white",
            colorSuccess: "#37B076",
            colorText: "black",
            colorBorder: "#DEE1E7",
            fontFamily: "Ubuntu",
            fontFamilyCode: "Ubuntu Mono",
          },
          components: {
            // .ant-btn-color-primary
            Button: {
              primaryShadow: "none",
            },
            Tag: {
              borderRadiusSM: 1,
            },
            Collapse: {
              colorFillAlter: "#FAFAFA",
              // borderRadiusLG: 0,
            },
            Result: {
              titleFontSize: 20,
              subtitleFontSize: 14,
            },
          },
        }}
        locale={locale}
      >
        <OutdatedBrowserAlert />
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <Suspense
            fallback={<FlexActivityIndicator style={{ height: "100vh" }} />}
          >
            <AuthProvider
              onLogin={() => {}}
              onLogout={() => {
                setRelayEnvironment(createRelayEnvironment());
              }}
              onInvalidToken={() => {
                setRelayEnvironment(createRelayEnvironment());
              }}
            >
              <Routes>
                <Route
                  path="/"
                  element={
                    <RequireAuth>
                      <MainLayout />
                    </RequireAuth>
                  }
                >
                  <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/pipelines/*" element={<PipelineList />} />
                  <Route
                    path="/pipelines/:pipelineId"
                    element={<PipelineList />}
                  />
                  <Route
                    path="/pipeline-jobs/*"
                    element={<PipelineJobsPage />}
                  />
                  <Route
                    path="pipeline-jobs/:jobId"
                    element={<PipelineJobDetail />}
                  />
                  <Route
                    path="/deployments/*"
                    element={<ModelDeploymentsPage />}
                  />
                  <Route path="/prompts/*" element={<PromptsPage />} />
                  <Route
                    path="/audit-logs/*"
                    element={
                      <RequirePrivilege
                        privileges={["superadmin", "admin"]}
                        fallback={<AccessDenied />}
                      >
                        <AuditLogsPage />
                      </RequirePrivilege>
                    }
                  />
                  <Route path="/settings/*" element={<SettingsPage />} />
                </Route>
                <Route path="login" element={<LoginPage />} />
                <Route path="*" element={<NoMatch />} />
              </Routes>
            </AuthProvider>
          </Suspense>
        </RelayEnvironmentProvider>
      </ConfigProvider>
    </AntdApp>
  );
};

export default App;
